import React, {useState, useEffect, useRef} from 'react';
import {Modal, Button, Form} from 'react-bootstrap';
import './ProfileModal.css';
import {FaThumbsUp} from 'react-icons/fa';
import {FaHeartCirclePlus} from "react-icons/fa6";
import {MdOutlineEmail} from "react-icons/md";
import {LuEye} from "react-icons/lu";
import {Ban, Flag} from 'lucide-react';
import {useNavigate} from 'react-router-dom';
import {apiClient} from '../api/ApiClient';

const ProfileModal = ({show, onHide, profileData, className = "custom-modal"}) => {
    const timeoutIdsRef = useRef([]);
    const isMountedRef = useRef(true);
    const [states, setStates] = useState({
        winkSuccess: false,
        favoriteSuccess: false,
        emailSuccess: false,
        blockSuccess: false,
        reportSuccess: false,
        hasWinked: false,
        hasFavorited: false,
        isBlocked: false,
        isReported: false,
        showEmailForm: false,
        emailText: ''
    });

    const navigate = useNavigate();

    
    // Record view when modal is shown with profile data
    // April 2025 this will enter a viewed in dbf 
    
    useEffect(() => {
        if (show && profileData && profileData[0]) {
            // Record the view
            recordProfileView(profileData[0]);
        }
    }, [show, profileData]);

    // Function to record profile view
    const recordProfileView = async (memberId) => {
        try {
            console.log('Recording view for profile ID:', memberId);
            
            const response = await apiClient.post('/rest/recordView', {
                viewedMemberId: memberId
            });
            
            console.log('View recorded response:', response.data);
        } catch (error) {
            console.error('Error recording profile view:', error);
        }
    };

    // Reset all states and clear timeouts when profile changes or modal is shown/hidden
    useEffect(() => {
        isMountedRef.current = true;
        
        const resetStates = () => {
            // Clear any existing timeouts
            timeoutIdsRef.current.forEach(timeoutId => clearTimeout(timeoutId));
            timeoutIdsRef.current = [];

            setStates({
                winkSuccess: false,
                favoriteSuccess: false,
                emailSuccess: false,
                blockSuccess: false,
                reportSuccess: false,
                hasWinked: false,
                hasFavorited: false,
                isBlocked: false,
                isReported: false,
                showEmailForm: false,
                emailText: ''
            });
        };

        resetStates();

        // Cleanup function to clear timeouts when component unmounts or dependencies change
        return () => {
            isMountedRef.current = false;
            timeoutIdsRef.current.forEach(timeoutId => clearTimeout(timeoutId));
            timeoutIdsRef.current = [];
        };
    }, [profileData, show]);

    if (!profileData) return null;

    const safeSetStates = (updater) => {
        if (isMountedRef.current) {
            setStates(updater);
        }
    };

    const handleBlockMember = async (e) => {
        e.preventDefault();
        if (states.isBlocked) return;
        
        try {
            const response = await apiClient.post('/rest/blockMember', {
                blockedMemberId: profileData[0]
            });

            if (response.status === 200) {
                safeSetStates(prev => ({
                    ...prev,
                    blockSuccess: true,
                    isBlocked: true
                }));
                const timeoutId = setTimeout(() => {
                    setStates(prev => ({
                        ...prev,
                        blockSuccess: false
                    }));
                }, 3000);
                timeoutIdsRef.current.push(timeoutId);
            }
        } catch (error) {
            console.error('Error blocking member:', error);
        }
    };

    const handleReportMember = async () => {
        if (states.isReported) return;
        
        try {
            const response = await apiClient.post('/rest/reportMember', {
                reportedMemberId: profileData[0]
            });

            if (response.status === 200) {
                setStates(prev => ({
                    ...prev,
                    reportSuccess: true,
                    isReported: true
                }));
                const timeoutId = setTimeout(() => {
                    setStates(prev => ({
                        ...prev,
                        reportSuccess: false
                    }));
                }, 3000);
                timeoutIdsRef.current.push(timeoutId);
            }
        } catch (error) {
            console.error('Error reporting member:', error);
        }
    };

    // UPDATED EMAIL SUBMIT FUNCTION to match ProfileViewComponent.jsx
    const handleEmailSubmit = async (e) => {
        e.preventDefault();
        try {
            // Modified to match the structure used in ProfileViewComponent.jsx
            const response = await apiClient.post('/rest/sendmail', {
                emailToMemberId: Number(profileData[0]),
                to: "dummy@dummy.com", // Required by DTO but not used in stored proc
                subject: "New Message", // Required by DTO but not used in stored proc
                body: states.emailText
            });

            if (response.data) {
                // Check the response status from the stored procedure if available
                if (response.data.emailstatus === 'BLOCKED_YOU') {
                    console.warn('This member has blocked you from sending emails.');
                } else if (response.data.emailstatus === 'YOU_BLOCKED') {
                    console.warn('You have blocked this member.');
                } else if (response.data.emailstatus === 'UPGRADED_BLOCK_FREE') {
                    console.warn('This member requires an upgraded account to email them.');
                } else {
                    // Default success case
                    setStates(prev => ({
                        ...prev,
                        emailSuccess: true,
                        showEmailForm: false,
                        emailText: ''
                    }));
                    const timeoutId = setTimeout(() => {
                        setStates(prev => ({
                            ...prev,
                            emailSuccess: false
                        }));
                    }, 3000);
                    timeoutIdsRef.current.push(timeoutId);
                }
            }
        } catch (error) {
            console.error('Error sending email:', error);
        }
    };

    const sendMemberAction = async (action, memberId) => {
        try {
            const response = await apiClient.post(`/rest/membercontact/${action}`, {
                updatedColumns: {
                    profile: [{
                        name: action === 'wink' ? 'P_MEMBER_ID_WINKED_AT' : 'P_MEMBER_ID_FAVORITED',
                        val: memberId.toString()
                    }]
                }
            });
            return response.data;
        } catch (error) {
            console.error(`Error sending ${action}:`, error.response?.data || error.message);
            return null;
        }
    };

    const handleWinkClick = async (e) => {
        e.preventDefault();
        if (states.hasWinked) return;

        const result = await sendMemberAction('wink', profileData[0]);
        if (result && !result.error) {
            setStates(prev => ({
                ...prev,
                winkSuccess: true,
                hasWinked: true
            }));
            const timeoutId = setTimeout(() => {
                setStates(prev => ({
                    ...prev,
                    winkSuccess: false
                }));
            }, 3000);
            timeoutIdsRef.current.push(timeoutId);
        }
    };

    const handleFavoriteClick = async (e) => {
        e.preventDefault();
        if (states.hasFavorited) return;

        const result = await sendMemberAction('favorite', profileData[0]);
        if (result && !result.error) {
            setStates(prev => ({
                ...prev,
                favoriteSuccess: true,
                hasFavorited: true
            }));
            const timeoutId = setTimeout(() => {
                setStates(prev => ({
                    ...prev,
                    favoriteSuccess: false
                }));
            }, 3000);
            timeoutIdsRef.current.push(timeoutId);
        }
    };

    const handleProfileClick = (e) => {
        e.preventDefault();
        const token = localStorage.getItem('token');

        if (token) {
            onHide();
            navigate(`/profile/view/${profileData[19]}/${profileData[0]}`);
        } else {
            navigate('/login');
        }
    };

    return (
        <Modal show={show} onHide={onHide} centered size="lg" className={className}>
            <Modal.Header closeButton>
                <Modal.Title>{profileData[1]}</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {states.showEmailForm && (
                    <div className={`email-form-container ${states.showEmailForm ? 'slide-down' : ''} mb-3`}>
                        <Form onSubmit={handleEmailSubmit}>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={states.emailText}
                                onChange={(e) => setStates(prev => ({...prev, emailText: e.target.value}))}
                                placeholder="Type your message here..."
                                className="email-textarea"
                            />
                            <div className="email-actions">
                                <Button 
                                    variant="outline-secondary" 
                                    size="sm" 
                                    onClick={() => setStates(prev => ({...prev, showEmailForm: false}))}
                                >
                                    Cancel
                                </Button>
                                <Button variant="primary" size="sm" type="submit">
                                    Send
                                </Button>
                            </div>
                        </Form>
                    </div>
                )}

                <div className="profile-modal-content">
                    <div className="profile-left-section">
                        <div className="round-image-container">
                            <img
                                src={`https://pics.netfixup.com/${profileData[28]}/${profileData[26]}`}
                                alt="Profile"
                                className="round-profile-image"
                            />
                        </div>
                    </div>

                    <div className="profile-right-section">
                        <div className="custom-detail-tabs">
                            <div className="custom-detail-tab">{profileData[9]}</div>
                        </div>
                        <div className="details-grid">
                            <div className="detail-item">
                                <div className="detail-label">
                                    {profileData[8]} {profileData[11] === 'M' ? 'Male' : 'Female'} - Seeking {profileData[13] === 'M' ? 'Male' : 'Female'}
                                </div>
                            </div>
                            <div className="detail-item">
                                <div className="detail-label">Location: {profileData[21]}, {profileData[22]}</div>
                            </div>
                            <div className="detail-item">
                                <div className="detail-label">Living: {profileData[34] || 'Not Selected'}</div>
                            </div>
                            <div className="detail-item">
                                <div className="detail-label">Relationship: {profileData[35] || 'Not Selected'}</div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="action-buttons-container">
                    <div className="action-button">
                        {states.blockSuccess && <div className="success-message">✓ Blocked</div>}
                        <Button 
                            className={`full-profile-link ${states.isBlocked ? 'disabled-button' : ''}`}
                            size="sm" 
                            onClick={handleBlockMember}
                            disabled={states.isBlocked}
                        >
                            <Ban color="black" size={16} /> {states.isBlocked ? 'Blocked' : 'Block'}
                        </Button>
                    </div>

                    <div className="action-button">
                        {states.reportSuccess && <div className="success-message">✓ Reported</div>}
                        <Button 
                            className={`full-profile-link ${states.isReported ? 'disabled-button' : ''}`}
                            size="sm" 
                            onClick={handleReportMember}
                            disabled={states.isReported}
                        >
                            <Flag color="black" size={16} /> {states.isReported ? 'Reported' : 'Report'}
                        </Button>
                    </div>

                    <div className="action-button">
                        {states.emailSuccess && <div className="success-message">✓ Email Sent</div>}
                        <Button 
                            className="full-profile-link" 
                            size="sm" 
                            onClick={() => setStates(prev => ({...prev, showEmailForm: true}))}
                        >
                            <MdOutlineEmail color="black" size={16} /> Email
                        </Button>
                    </div>

                    <div className="action-button">
                        {states.favoriteSuccess && <div className="success-message">✓ Favorited</div>}
                        <Button
                            className={`full-profile-link ${states.hasFavorited ? 'disabled-button' : ''}`}
                            size="sm"
                            onClick={handleFavoriteClick}
                            disabled={states.hasFavorited}
                        >
                            <LuEye color="black" size={16} /> {states.hasFavorited ? 'Favorited' : 'Favorite'}
                        </Button>
                    </div>

                    <div className="action-button">
                        {states.winkSuccess && <div className="success-message">✓ Winked</div>}
                        <Button
                            className={`full-profile-link ${states.hasWinked ? 'disabled-button' : ''}`}
                            size="sm"
                            onClick={handleWinkClick}
                            disabled={states.hasWinked}
                        >
                            <LuEye color="black" size={16} /> {states.hasWinked ? 'Winked' : 'Wink'}
                        </Button>
                    </div>

                    <div className="action-button">
                        <Button className="full-profile-link" size="sm" onClick={handleProfileClick}>
                            <LuEye color="black" size={16} /> Profile
                        </Button>
                    </div>
                </div>
            </Modal.Body>
        </Modal>
    );
};

export default ProfileModal;